import { ReactNode } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import MainLayout from '../layouts/MainLayout';
import { DomainKey } from '../config/domains';

// Hub landing
import CommunicationsPage from '../pages/admin/Communications/index';

// WhatsApp channel pages
import {
  WhatsAppDashboardPage,
  WhatsAppComposePage,
  WhatsAppBulkSendPage,
  WhatsAppCampaignsPage,
  WhatsAppCampaignDetailPage,
  WhatsAppOutboxPage,
  WhatsAppInboxPage,
  WhatsAppTemplatesPage,
  WhatsAppProfilePage,
  WhatsAppDiagnosticsPage,
  WhatsAppBillingPage,
  WhatsAppSettingsPage,
} from '../pages/admin/Communications/WhatsApp/WhatsAppPages';
import { FlowsListPage } from '../pages/admin/Communications/WhatsApp/Flows/FlowsListPage';
import { FlowEditorPage } from '../pages/admin/Communications/WhatsApp/Flows/FlowEditorPage';
import { FlowDetailPage } from '../pages/admin/Communications/WhatsApp/Flows/FlowDetailPage';
// WhatsApp templates: the library + message links, and the assisted creator
import WhatsAppTemplatesLibraryPage from '../pages/admin/Communications/WhatsApp/templates/WhatsAppTemplatesLibraryPage';
import WhatsAppTemplateCreatorPage from '../pages/admin/Communications/WhatsApp/templates/WhatsAppTemplateCreatorPage';

// Email channel pages
import {
  EmailDashboardPage,
  EmailComposePage,
  EmailBulkSendPage,
  EmailCampaignsPage,
  EmailCampaignDetailPage,
  EmailOutboxPage,
  EmailTemplatesPage,
  EmailSettingsPage,
} from '../pages/admin/Communications/Email/EmailPages';
import EmailDetailPage from '../pages/admin/Communications/Email/view/[id]/index';

// SMS channel pages
import {
  SmsDashboardPage,
  SmsComposePage,
  SmsBulkSendPage,
  SmsCampaignsPage,
  SmsCampaignDetailPage,
  SmsOutboxPage,
  SmsTemplatesPage,
  SmsSettingsPage,
} from '../pages/admin/Communications/SMS/SmsPages';

// Push channel pages
import {
  PushDashboardPage,
  PushComposePage,
  PushBulkSendPage,
  PushCampaignsPage,
  PushCampaignDetailPage,
  PushOutboxPage,
  PushTemplatesPage,
  PushSettingsPage,
} from '../pages/admin/Communications/PushNotification/PushPages';

/**
 * Communications Routes
 *
 * Mounted as a nested splat at /admin/communications/* (see AdminRoutes.tsx).
 * The hub landing keeps the global sidebar; each channel's pages are wrapped in
 * MainLayout with the channel's own domainKey (comms-whatsapp / comms-email /
 * comms-sms / comms-push), which renders that channel's dedicated left sidebar
 * from its menu provider — so every channel behaves like a domain app.
 *
 * Channels are `hidden` domains (config/domains.tsx), so they never appear in the
 * global domain switcher/grid — Communications stays the single top-level entry.
 */
export default function CommunicationsRoutes() {
  const wrap = (key: DomainKey, el: ReactNode) => <MainLayout domainKey={key}>{el}</MainLayout>;
  const wa = (el: ReactNode) => wrap('comms-whatsapp', el);
  const em = (el: ReactNode) => wrap('comms-email', el);
  const sm = (el: ReactNode) => wrap('comms-sms', el);
  const pu = (el: ReactNode) => wrap('comms-push', el);

  return (
    <Routes>
      {/* Hub landing — global sidebar (no domainKey) */}
      <Route index element={<MainLayout><CommunicationsPage /></MainLayout>} />

      {/* ---- WhatsApp ---- */}
      <Route path="whatsapp" element={wa(<WhatsAppDashboardPage />)} />
      <Route path="whatsapp/compose" element={wa(<WhatsAppComposePage />)} />
      <Route path="whatsapp/bulk" element={wa(<WhatsAppBulkSendPage />)} />
      <Route path="whatsapp/campaigns" element={wa(<WhatsAppCampaignsPage />)} />
      <Route path="whatsapp/campaigns/:id" element={wa(<WhatsAppCampaignDetailPage />)} />
      <Route path="whatsapp/outbox" element={wa(<WhatsAppOutboxPage />)} />
      <Route path="whatsapp/inbox" element={wa(<WhatsAppInboxPage />)} />
      <Route path="whatsapp/templates" element={wa(<WhatsAppTemplatesLibraryPage />)} />
      <Route path="whatsapp/templates/new" element={wa(<WhatsAppTemplateCreatorPage />)} />
      {/* The 360dialog-only template list (create, delete) the library page replaced. */}
      <Route path="whatsapp/templates/360dialog" element={wa(<WhatsAppTemplatesPage />)} />
      <Route path="whatsapp/flows" element={wa(<FlowsListPage />)} />
      <Route path="whatsapp/flows/new" element={wa(<FlowEditorPage />)} />
      <Route path="whatsapp/flows/:id" element={wa(<FlowDetailPage />)} />
      <Route path="whatsapp/flows/:id/edit" element={wa(<FlowEditorPage />)} />
      <Route path="whatsapp/profile" element={wa(<WhatsAppProfilePage />)} />
      <Route path="whatsapp/diagnostics" element={wa(<WhatsAppDiagnosticsPage />)} />
      <Route path="whatsapp/billing" element={wa(<WhatsAppBillingPage />)} />
      <Route path="whatsapp/settings" element={wa(<WhatsAppSettingsPage />)} />

      {/* ---- Email ---- */}
      <Route path="email" element={em(<EmailDashboardPage />)} />
      <Route path="email/compose" element={em(<EmailComposePage />)} />
      <Route path="email/bulk" element={em(<EmailBulkSendPage />)} />
      <Route path="email/campaigns" element={em(<EmailCampaignsPage />)} />
      <Route path="email/campaigns/:id" element={em(<EmailCampaignDetailPage />)} />
      <Route path="email/outbox" element={em(<EmailOutboxPage />)} />
      <Route path="email/outbox/view/:id" element={em(<EmailDetailPage />)} />
      <Route path="email/view/:id" element={em(<EmailDetailPage />)} />
      <Route path="email/templates" element={em(<EmailTemplatesPage />)} />
      <Route path="email/settings" element={em(<EmailSettingsPage />)} />

      {/* ---- SMS ---- */}
      <Route path="sms" element={sm(<SmsDashboardPage />)} />
      <Route path="sms/compose" element={sm(<SmsComposePage />)} />
      <Route path="sms/bulk" element={sm(<SmsBulkSendPage />)} />
      <Route path="sms/campaigns" element={sm(<SmsCampaignsPage />)} />
      <Route path="sms/campaigns/:id" element={sm(<SmsCampaignDetailPage />)} />
      <Route path="sms/outbox" element={sm(<SmsOutboxPage />)} />
      <Route path="sms/templates" element={sm(<SmsTemplatesPage />)} />
      <Route path="sms/settings" element={sm(<SmsSettingsPage />)} />

      {/* ---- Push ---- */}
      <Route path="push" element={pu(<PushDashboardPage />)} />
      <Route path="push/compose" element={pu(<PushComposePage />)} />
      <Route path="push/bulk" element={pu(<PushBulkSendPage />)} />
      <Route path="push/campaigns" element={pu(<PushCampaignsPage />)} />
      <Route path="push/campaigns/:id" element={pu(<PushCampaignDetailPage />)} />
      <Route path="push/outbox" element={pu(<PushOutboxPage />)} />
      <Route path="push/templates" element={pu(<PushTemplatesPage />)} />
      <Route path="push/settings" element={pu(<PushSettingsPage />)} />

      {/* Unknown sub-path -> hub */}
      <Route path="*" element={<Navigate to="/admin/communications" replace />} />
    </Routes>
  );
}
